/* eslint-disable prettier/prettier */
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import Select from "react-select";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import AdminServices from "services/admin-services";
import { useEffect, useState } from "react";
import Loader from "common/Loader";
import InvestorService from "services/invoice-service";
import { setToaster } from "common/toastStore";

interface ISelectOption {
  label: string;
  value: number;
  packageData?: any;
}

const getOptionPackageId = (pkg: any): number => {
  const resolvedId =
    pkg?.packageId ?? pkg?.package?.id ?? pkg?.id ?? pkg?.investorPackageId;

  return Number(resolvedId || 0);
};

const getResolvedPackageIdFromOption = (option?: ISelectOption, fallback?: any) => {
  const resolvedId =
    option?.packageData?.package?.id ??
    option?.packageData?.packageId ??
    option?.packageData?.id ??
    option?.value ??
    fallback;

  return Number(resolvedId || 0);
};

interface IForm {
  packageName: ISelectOption;
  contentType: number;
  mergeOption: number;
  outputFileName: string;
  folderName: string;
  zipPackage: boolean;
  zipFileName: string;
  isActive: boolean;
}

const DEFAULT_OUTPUT_FILE_NAME = "{Loan.LoanNumber}_{Fields.4002}.pdf";
const DELIVERY_CONFIGURATION_KEYS = [
  "defaultDeliveryMethods",
  "defaultDeliveryConfiguration",
  "deliveryConfiguration",
  "deliveryConfiguraiton",
  "deliveryOptions",
];
const OUTPUT_FILE_KEYS = ["outputFileFormat", "outputFileName"];
const DELIVERY_FIELD_HINTS = [
  "deliveryType",
  "path",
  "url",
  "port",
  "userName",
  "password",
  "privateKeyFile",
  "passPhrase",
  "subFolderName",
];

const removeClientRowId = (delivery: any) => {
  if (!delivery || typeof delivery !== "object") return delivery;

  const sanitizedDelivery = { ...delivery };
  delete sanitizedDelivery._rowId;

  return sanitizedDelivery;
};

const tryParseJson = (value: any) => {
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const toArray = (value: any) => {
  const parsedValue = tryParseJson(value);

  if (Array.isArray(parsedValue)) return parsedValue;
  if (!parsedValue) return [];

  return [parsedValue];
};

const getFirstDefinedValue = (source: any, keys: string[]) => {
  if (!source || typeof source !== "object") return undefined;

  return keys.find((key) => source[key] !== undefined)
    ? source[keys.find((key) => source[key] !== undefined) as string]
    : undefined;
};

const collectObjectsDeep = (source: any) => {
  const parsedSource = tryParseJson(source);
  const queue: any[] = [parsedSource];
  const objects: any[] = [];
  const seen = new WeakSet<object>();

  while (queue.length > 0) {
    const current = tryParseJson(queue.shift());

    if (!current || typeof current !== "object") continue;

    if (seen.has(current)) continue;
    seen.add(current);

    objects.push(current);

    if (Array.isArray(current)) {
      current.forEach((item) => queue.push(item));
      continue;
    }

    Object.values(current).forEach((value) => queue.push(value));
  }

  return objects;
};

const isDeliveryLikeObject = (value: any) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;

  return DELIVERY_FIELD_HINTS.some((key) => value[key] !== undefined);
};

const normalizeDeliveryCandidates = (value: any) => {
  const parsedValue = tryParseJson(value);

  if (Array.isArray(parsedValue)) {
    return parsedValue.filter(isDeliveryLikeObject);
  }

  if (isDeliveryLikeObject(parsedValue)) {
    return [parsedValue];
  }

  return [];
};

const extractDeliveryConfigurationByHeuristics = (
  source: any,
  packageId?: number,
  mergeOption?: number,
) => {
  const deepObjects = collectObjectsDeep(source);
  const passes = [
    {
      package: packageId !== undefined && packageId !== null,
      merge: mergeOption !== undefined && mergeOption !== null,
    },
    { package: packageId !== undefined && packageId !== null, merge: false },
    { package: false, merge: mergeOption !== undefined && mergeOption !== null },
    { package: false, merge: false },
  ];

  for (const pass of passes) {
    for (const candidate of deepObjects) {
      if (!candidate || typeof candidate !== "object") continue;

      if (pass.package) {
        if (String(getPackageKey(candidate)) !== String(packageId)) continue;
      }

      if (pass.merge) {
        if (String(getMergeOptionKey(candidate)) !== String(mergeOption)) continue;
      }

      const directCandidates = normalizeDeliveryCandidates(candidate);
      if (directCandidates.length > 0) return directCandidates;

      if (Array.isArray(candidate)) continue;

      for (const nestedValue of Object.values(candidate)) {
        const nestedCandidates = normalizeDeliveryCandidates(nestedValue);
        if (nestedCandidates.length > 0) return nestedCandidates;
      }
    }
  }

  return [];
};

const getPackageSettings = (basicConfiguration: any) => {
  const parsedConfiguration = tryParseJson(basicConfiguration);

  return toArray(
    parsedConfiguration?.packageSettings ??
    parsedConfiguration?.packageSetting ??
    parsedConfiguration?.packageConfigurations ??
    parsedConfiguration?.packageConfiguration,
  );
};

const getMergeOptionKey = (setting: any) =>
  setting?.mergeOption ??
  setting?.mergeOptionId ??
  setting?.mergeTypeId ??
  setting?.mergeType;

const getPackageKey = (setting: any) =>
  setting?.packageId ??
  setting?.investorPackageId ??
  setting?.templatePackageId ??
  setting?.id;

const getMatchedPackageSetting = (
  source: any,
  packageId?: number,
  mergeOption?: number,
) => {
  const parsedSource = tryParseJson(source);
  const packageSettings = getPackageSettings(parsedSource);
  const deepObjects = collectObjectsDeep(parsedSource);

  if (!packageSettings.length) {
    return null;
  }

  const hasPackage = packageId !== undefined && packageId !== null;
  const hasMerge = mergeOption !== undefined && mergeOption !== null;

  if (hasPackage && hasMerge) {
    const exactMatch = packageSettings.find((setting: any) => {
      const settingPackageKey = getPackageKey(setting);
      const settingMergeKey = getMergeOptionKey(setting);

      return (
        String(settingPackageKey) === String(packageId) &&
        String(settingMergeKey) === String(mergeOption)
      );
    });

    if (exactMatch) return exactMatch;

    const deepExactMatch = deepObjects.find((setting: any) => {
      const settingPackageKey = getPackageKey(setting);
      const settingMergeKey = getMergeOptionKey(setting);

      return (
        String(settingPackageKey) === String(packageId) &&
        String(settingMergeKey) === String(mergeOption)
      );
    });

    if (deepExactMatch) return deepExactMatch;
  }

  if (hasPackage) {
    const packageMatch = packageSettings.find(
      (setting: any) => String(getPackageKey(setting)) === String(packageId),
    );

    if (packageMatch) return packageMatch;

    const deepPackageMatch = deepObjects.find(
      (setting: any) => String(getPackageKey(setting)) === String(packageId),
    );

    if (deepPackageMatch) return deepPackageMatch;
  }

  if (hasMerge) {
    const mergeMatch = packageSettings.find(
      (setting: any) =>
        String(getMergeOptionKey(setting)) === String(mergeOption),
    );

    if (mergeMatch) return mergeMatch;

    const deepMergeMatch = deepObjects.find(
      (setting: any) =>
        String(getMergeOptionKey(setting)) === String(mergeOption),
    );

    if (deepMergeMatch) return deepMergeMatch;
  }

  return packageSettings[0] || null;
};

const getDefaultDeliveryConfiguration = (
  source: any,
  packageId?: number,
  mergeOption?: number,
) => {
  const parsedConfiguration = tryParseJson(source);
  const matchedSetting = getMatchedPackageSetting(
    parsedConfiguration,
    packageId,
    mergeOption,
  );

  const rawDefaults =
    getFirstDefinedValue(matchedSetting, DELIVERY_CONFIGURATION_KEYS) ??
    getFirstDefinedValue(parsedConfiguration, DELIVERY_CONFIGURATION_KEYS) ??
    [];

  let normalized = toArray(rawDefaults);

  if (!normalized.length) {
    const deepObjects = collectObjectsDeep(parsedConfiguration);
    const fallbackSetting = deepObjects.find((setting: any) => {
      const packageMatches =
        packageId === undefined ||
        packageId === null ||
        String(getPackageKey(setting)) === String(packageId);
      const mergeMatches =
        mergeOption === undefined ||
        mergeOption === null ||
        String(getMergeOptionKey(setting)) === String(mergeOption);

      if (!packageMatches || !mergeMatches) return false;

      return getFirstDefinedValue(setting, DELIVERY_CONFIGURATION_KEYS) !== undefined;
    });

    if (fallbackSetting) {
      normalized = toArray(
        getFirstDefinedValue(fallbackSetting, DELIVERY_CONFIGURATION_KEYS),
      );
    }
  }

  if (!normalized.length) {
    normalized = extractDeliveryConfigurationByHeuristics(
      parsedConfiguration,
      packageId,
      mergeOption,
    );
  }

  return normalized.map(removeClientRowId);
};

const getOutputFileFormatFromSource = (
  source: any,
  packageId?: number,
  mergeOption?: number,
) => {
  const matchedSetting = getMatchedPackageSetting(source, packageId, mergeOption);
  const directValue = getFirstDefinedValue(matchedSetting, OUTPUT_FILE_KEYS);

  if (directValue !== undefined && directValue !== null && String(directValue).trim()) {
    return String(directValue);
  }

  const deepObjects = collectObjectsDeep(source);
  const fallbackSetting = deepObjects.find((setting: any) => {
    const packageMatches =
      packageId === undefined ||
      packageId === null ||
      String(getPackageKey(setting)) === String(packageId);
    const mergeMatches =
      mergeOption === undefined ||
      mergeOption === null ||
      String(getMergeOptionKey(setting)) === String(mergeOption);

    if (!packageMatches || !mergeMatches) return false;

    const value = getFirstDefinedValue(setting, OUTPUT_FILE_KEYS);
    return value !== undefined && value !== null && String(value).trim();
  });

  return getFirstDefinedValue(fallbackSetting, OUTPUT_FILE_KEYS) || "";
};

const applyDeliverySubFolderRules = (
  deliveries: any[],
  selectedInvestor: any,
) => {
  if (!Array.isArray(deliveries) || deliveries.length === 0) return deliveries;

  const externalInvestorName = (selectedInvestor?.externalInvestorName || "").trim();
  const isBondInvestor = Boolean(selectedInvestor?.isBondInvestor);

  return deliveries.map((delivery: any) => {
    const sanitized = removeClientRowId(delivery);

    if (!sanitized || typeof sanitized !== "object") return sanitized;

    if (Number(sanitized.deliveryType) === 1) {
      return {
        ...sanitized,
        subFolderName: externalInvestorName,
      };
    }

    if (Number(sanitized.deliveryType) === 2) {
      return {
        ...sanitized,
        subFolderName: isBondInvestor ? "" : externalInvestorName,
      };
    }

    return sanitized;
  });
};

const schema = Yup.object().shape({
  packageName: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.number().required(),
    })
    .required("Package Name is required"),
  contentType: Yup.number().required("Output File Type is required"),
  mergeOption: Yup.number().required("Merge Option is required"),
  outputFileName: Yup.string(),
  folderName: Yup.string(),
  zipPackage: Yup.boolean(),
  isActive: Yup.boolean(),
  zipFileName: Yup.string().when("zipPackage", {
    is: true,
    then: (schema) =>
      schema.required("Zip File Name required when zip enabled"),
  }),
});

const PackageConfigs = ({
  contentTypes,
  mergeOptions,
  onHide,
  onSuccess,
  mode,
  data,
  selectedInvestor,
  basicConfiguration,
}: any) => {
  const [packageOptions, setPackageOptions] = useState<ISelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<IForm>({
    mode: "onSubmit",
    resolver: yupResolver(schema),
    defaultValues: {
      zipPackage: false,
      isActive: true,
    },
  });

  const values = watch();
  const zipEnabled = values.zipPackage;
  const mergeOptionValue = values.mergeOption;
  const selectedPackageId = values.packageName?.value;

  console.log("values:", values);

  // Auto-populate output file name based on merge option from basicConfiguration
  useEffect(() => {
    if (!mergeOptionValue || mode === "edit") return;

    const selectedPackage = packageOptions.find(
      (pkg: ISelectOption) => String(pkg?.value) === String(selectedPackageId),
    );
    const sources = [selectedPackage?.packageData, basicConfiguration];

    let matchingSetting: any = null;
    let resolvedOutputFileName = "";

    for (const source of sources) {
      const matched = getMatchedPackageSetting(
        source,
        selectedPackageId,
        mergeOptionValue,
      );

      if (matched) {
        matchingSetting = matched;
      }

      if (!resolvedOutputFileName) {
        resolvedOutputFileName = getOutputFileFormatFromSource(
          source,
          selectedPackageId,
          mergeOptionValue,
        );
      }

      if (matchingSetting && resolvedOutputFileName) {
        break;
      }
    }

    if (mode === "create") {
      const outputFileFormat =
        resolvedOutputFileName ||
        getFirstDefinedValue(matchingSetting, OUTPUT_FILE_KEYS);

      setValue("outputFileName", outputFileFormat || "", {
        shouldValidate: false,
      });
    }
  }, [
    mergeOptionValue,
    selectedPackageId,
    packageOptions,
    basicConfiguration,
    mode,
    setValue,
  ]);

  const onSubmit = (formData: IForm) => {
    setLoading(true);
    const outputFileName =
      formData.outputFileName?.trim() || DEFAULT_OUTPUT_FILE_NAME;
    const selectedPackage = packageOptions.find(
      (pkg: ISelectOption) =>
        String(pkg?.value) === String(formData.packageName?.value),
    );
    const resolvedPackageId = getResolvedPackageIdFromOption(
      selectedPackage,
      data?.packageMasterId ?? formData.packageName?.value,
    );
    const defaultDeliverySources = [selectedPackage?.packageData, basicConfiguration];
    let defaultDeliveryConfiguration: any[] = [];

    if (mode === "create") {
      for (const source of defaultDeliverySources) {
        const resolvedDefaults = getDefaultDeliveryConfiguration(
          source,
          resolvedPackageId,
          formData.mergeOption,
        );

        if (resolvedDefaults.length > 0) {
          defaultDeliveryConfiguration = resolvedDefaults;
          break;
        }
      }
    }

    const resolvedCreateDeliveryConfiguration = applyDeliverySubFolderRules(
      defaultDeliveryConfiguration,
      selectedInvestor,
    );

    const payload = {
      id: mode === "edit" ? data?.id : 0,
      isActive: formData.isActive ?? true,
      packageName: formData.packageName.label,
      investorPackageId: mode === "edit" ? data?.investorPackageId : 0,
      packageId: resolvedPackageId,
      investorId: selectedInvestor?.id,
      bundleConfiguration: JSON.stringify({
        contentType: formData.contentType,
        mergeOption: formData.mergeOption,
        outputFileName,
        folderName: formData.folderName,
        zipPackage: formData.zipPackage,
        zipFileName: formData.zipFileName,
      }),
      deliveryConfiguraiton:
        mode === "edit"
          ? data?.deliveryOptions
            ? JSON.stringify(data.deliveryOptions.map(removeClientRowId))
            : ""
          : JSON.stringify(resolvedCreateDeliveryConfiguration),
    };

    const apiCall =
      mode === "edit"
        ? InvestorService.updateInvestorsPackage
        : InvestorService.createInvestorsPackageList;

    apiCall(payload)
      .then(() => {
        setLoading(false);
        onSuccess();
        setToaster({
          severity: "success",
          summary: "Success",
          detail:
            mode === "edit"
              ? "Package updated successfully"
              : "Package created successfully",
        });
      })
      .catch((err: any) => {
        setLoading(false);
        setToaster({
          severity: "error",
          summary: "Error",
          detail: err?.response?.data || "Operation failed",
        });
      });
  };

  const getPackageNamesbyInvestorID = () => {
    AdminServices.getInvestorPackages()
      .then((response: any) => {
        const packages = (response?.data || []).map((pkg: any) => ({
          label: pkg?.name || pkg?.package?.name,
          value: getOptionPackageId(pkg),
          packageData: pkg,
        }));

        setPackageOptions(packages);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getPackageNamesbyInvestorID();
  }, []);

  useEffect(() => {
    if (mode === "edit" && data) {
      reset({
        packageName: {
          label: data.packageName,
          value: data.packageMasterId ?? data.packageId,
        },
        contentType: data.contentType,
        mergeOption: data.mergeOption,
        outputFileName: data.outputFileName || DEFAULT_OUTPUT_FILE_NAME,
        folderName: data.folderName,
        zipPackage: data.zipPackage,
        zipFileName: data.zipFileName,
        isActive: data.isActive ?? true,
      });
    }

    if (mode === "create") {
      reset({
        packageName: undefined,
        contentType: undefined,
        mergeOption: undefined,
        outputFileName: "",
        folderName: "",
        zipPackage: false,
        zipFileName: "",
        isActive: true,
      });
    }
  }, [mode, data, reset]);

  useEffect(() => {
    if (mode !== "edit" || !data || !packageOptions.length) return;

    const currentValue = values.packageName?.value;
    const selectedByValue = packageOptions.find(
      (pkg: ISelectOption) => String(pkg.value) === String(currentValue),
    );

    if (selectedByValue) return;

    const matchedOption = packageOptions.find(
      (pkg: ISelectOption) =>
        String(pkg?.label || "").trim().toLowerCase() ===
        String(data?.packageName || "").trim().toLowerCase(),
    );

    if (!matchedOption) return;

    setValue("packageName", matchedOption, { shouldValidate: true });
  }, [mode, data, packageOptions, values.packageName, setValue]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-5">
        {/* Package Name */}
        <div>
          <label className="text-sm py-1 block">Package Name</label>
          <Select
            options={packageOptions}
            value={values.packageName}
            onChange={(selected: any) =>
              setValue("packageName", selected, { shouldValidate: true })
            }
          />
          <div className="text-xs text-red-900">
            {errors?.packageName?.message}
          </div>
        </div>

        {/* Output File Type */}
        <div>
          <label className="text-sm py-1 block">Output File Type</label>
          <Select
            options={contentTypes?.map((c: any) => ({
              label: c.name,
              value: c.id,
            }))}
            value={contentTypes
              ?.map((c: any) => ({ label: c.name, value: c.id }))
              ?.find((o: any) => o.value === values.contentType)}
            onChange={(selected: any) =>
              setValue("contentType", selected?.value, { shouldValidate: true })
            }
          />
          <div className="text-xs text-red-900">
            {errors?.contentType?.message}
          </div>
        </div>

        {/* Merge Option */}
        <div>
          <label className="text-sm py-1 block">Merge Option</label>
          <Select
            options={mergeOptions?.map((m: any) => ({
              label: m.name,
              value: m.id,
            }))}
            value={mergeOptions
              ?.map((m: any) => ({ label: m.name, value: m.id }))
              ?.find((o: any) => o.value === values.mergeOption)}
            onChange={(selected: any) =>
              setValue("mergeOption", selected?.value, { shouldValidate: true })
            }
          />
          <div className="text-xs text-red-900">
            {errors?.mergeOption?.message}
          </div>
        </div>

        {/* Output File Name */}
        <div>
          <label className="text-sm py-1 block">Output File Name</label>
          <InputText
            {...register("outputFileName")}
            placeholder="Auto-populated based on merge option"
            className="w-full"
          />
          <div className="text-xs text-red-900">
            {errors?.outputFileName?.message}
          </div>
        </div>

        {/* Folder Name */}
        <div>
          <label className="text-sm py-1 block">Folder Name</label>
          <InputText
            {...register("folderName")}
            placeholder="Optional folder name"
            className="w-full"
          />
        </div>

        {/* Zip Package */}
        <div className="flex items-center gap-3">
          <Checkbox
            inputId="zipPackage"
            checked={values.zipPackage}
            onChange={(e) =>
              setValue("zipPackage", e.checked ?? false, {
                shouldValidate: true,
              })
            }
          />
          <label htmlFor="zipPackage">Zip Package</label>
        </div>

        <div className="flex items-center gap-3">
          <Checkbox
            inputId="isActive"
            checked={values.isActive}
            onChange={(e) =>
              setValue("isActive", e.checked ?? false, {
                shouldValidate: true,
              })
            }
          />
          <label htmlFor="isActive">Is Active</label>
        </div>

        {/* Zip File Name */}
        {zipEnabled && (
          <div>
            <label className="text-sm py-1 block">Zip File Name</label>
            <InputText
              {...register("zipFileName")}
              placeholder="e.g., archive.zip"
              className="w-full"
            />
            <div className="text-xs text-red-900">
              {errors?.zipFileName?.message}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            label="Cancel"
            icon="pi pi-times"
            onClick={onHide}
            className="p-button-sm p-button-danger p-button-outlined"
          />
          <Button
            label={mode === "edit" ? "Update" : "Create"}
            type="submit"
            icon="pi pi-check"
            className="p-button-sm p-button-success p-button-outlined"
          />
        </div>
      </form>
      {loading && <Loader />}
    </>
  );
};

export default PackageConfigs;
